import { getExploitabilityByKeys } from "@/const/insights-constants"
import {
  EXPLOITABILITY_FILTER_OPTIONS,
  EXPLOITABILITY_VERDICT_SEARCH_OPTIONS
} from "@/const/options.constants"
import { RESOURCES_NAMES } from "@/const/resource.constants"
import * as ApplicationDomain from "@/domain/application"
import * as DataFlowDomain from "@/domain/data-flow"
import * as IssueDomain from "@/domain/issue"
import { isOnPremSourceControl } from "@/domain/resource/resource"
import {
  getIssuesCountMapOrderedSeverity,
  type Severity
} from "@/domain/vulnerability"
import {
  DEFAULT_LONG_FORMAT,
  getDateFromSeconds,
  getDateInUserLocale,
  getRelativeTimeFromNow
} from "@/helpers/date.helpers"
import * as filtersHelper from "@/helpers/filters.helper"
import { INEQUALITY_SYMBOL } from "@/organisms/SmartSearch/const/symbol.constants"

import type { DataApplication } from "@/api/useInventory.types"
import type {
  IssuesTriageResponse,
  ProjectsTriageResponse
} from "@/api/useTriage.types"
import type { DataFlow, Flows } from "@/domain/data-flow/data-flow.types"
import type {
  Application,
  FindingKai,
  FixLocation,
  Issue,
  Project,
  ProjectAction,
  Ticket
} from "@/domain/issue"
import type { Label } from "@/domain/label"
import { getTicketingSystemsFromUrls } from "@/domain/ticketing"
import type { QueryClient } from "@/hooks/useHttp"
import type { TFunction } from "@/hooks/useTranslation"
import type {
  Filters,
  Option
} from "@/organisms/SmartSearch/types/options.types"
import type { IssueDTO } from "./useTriage.types"

export const getDiscoveredDaysAgo = (timestampInSeconds: number) => {
  const date = getDateFromSeconds(timestampInSeconds)

  return {
    timestamp: timestampInSeconds,
    text: getRelativeTimeFromNow(date),
    tooltip: getDateInUserLocale(date, DEFAULT_LONG_FORMAT)
  }
}

export const hasKaiAnalysis = (issue: IssueDTO) => {
  const { aggregatedKaiFindings } = issue || {}
  const { irrelevant, positive, undetermined } = aggregatedKaiFindings || {}

  return (
    irrelevant?.length > 0 || positive?.length > 0 || undetermined?.length > 0
  )
}

export const hasKaiPositiveFindings = (issue: IssueDTO) => {
  const { aggregatedKaiFindings } = issue || {}
  const { positive } = aggregatedKaiFindings || {}

  return positive?.length > 0
}

export const hasOnlyIrrelevantKaiFindings = (issue: IssueDTO) => {
  const { aggregatedKaiFindings } = issue || {}
  const { irrelevant, positive, undetermined } = aggregatedKaiFindings || {}

  return irrelevant?.length > 0 && !positive?.length && !undetermined?.length
}

export const getApplicationsMetaData = (
  applications: (
    | Application
    | Pick<
        DataApplication,
        | "name"
        | "environment"
        | "namespace"
        | "resourceIds"
        | "applicationId"
        | "archivedAt"
        | "workloadKind"
        | "workloadStatus"
      >
  )[],
  params: { showAllApplications?: boolean; noData?: boolean } = {}
) => {
  const { index, ...meta } = applications.reduce(
    (acc, application, index) => {
      acc.total += 1
      acc.index.push(index)

      switch (application.workloadStatus) {
        case "active":
          acc.active += 1

          return acc
        case "inactive":
          acc.inactive += 1

          return acc
        case "terminated":
          acc.terminated += 1

          return acc
      }

      return acc
    },
    {
      active: 0,
      inactive: 0,
      terminated: 0,
      total: 0,
      index: []
    } as {
      active: number
      inactive: number
      terminated: number
      total: number
      index: number[]
    }
  )

  return {
    items: index
      .filter((appIndex) => {
        const status = applications[appIndex]?.workloadStatus

        return (
          !!status && status !== ApplicationDomain.WORKLOAD_STATUS.TERMINATED
        )
      })
      .sort((firstIndex, secondIndex) => {
        const a = applications?.[firstIndex]
        const b = applications?.[secondIndex]

        if (!a || !b) return 0

        const aIsArchived = a.archivedAt !== null && a.archivedAt !== undefined
        const bIsArchived = b.archivedAt !== null && b.archivedAt !== undefined

        if (aIsArchived && !bIsArchived) return 1
        if (!aIsArchived && bIsArchived) return -1

        if (a.workloadStatus === "active" && b.workloadStatus !== "active")
          return -1
        if (a.workloadStatus !== "active" && b.workloadStatus === "active")
          return 1
        if (a.workloadStatus === "inactive" && b.workloadStatus !== "inactive")
          return -1
        if (a.workloadStatus !== "inactive" && b.workloadStatus === "inactive")
          return 1
        if (
          a.workloadStatus === "terminated" &&
          b.workloadStatus !== "terminated"
        )
          return -1
        if (
          a.workloadStatus !== "terminated" &&
          b.workloadStatus === "terminated"
        )
          return 1

        return 0
      }),
    environments: Array.from(
      new Set(
        applications.map((application) =>
          ApplicationDomain.getDescription(application)
        )
      )
    ),
    ...meta,
    ...params
  }
}

export const getProjectsTriageSelector = (response: ProjectsTriageResponse) => {
  const { data, ...restResponseData } = response || {}

  if (!data) return response as ProjectsTriageResponse & { data: undefined }

  return {
    data: (Array.isArray(data) ? data : [data]).map((item) => {
      const { riskInsights, ...restItem } = item
      const {
        id: key,
        actions,
        resources,
        issuesSummary,
        discoveredAt,
        relatedApplications
      } = restItem
      const firstResource = resources[0] || {
        name: "",
        sourceControl: "",
        instanceId: "",
        type: ""
      }
      const { sourceControl } = firstResource
      const issuesCounts = getIssuesCountMapOrderedSeverity(issuesSummary)

      const ticketSystems = getProjectTicketSystems(actions)
      const hasTicketAction = ticketSystems.length > 0
      const hasPullRequestAction = actions?.some(
        (action) => action.type === "pullRequest"
      )

      return {
        key,
        sourceControl,
        quickWinsCount: restItem.quickWinsCount || 0,
        quickWinsTotalIssues:
          restItem.quickWinsByType?.reduce(
            (sum: number, t: { totalIssues: number }) =>
              sum + (t.totalIssues || 0),
            0
          ) ?? 0,
        severity: issuesCounts,
        applicationCount: getApplicationsMetaData(relatedApplications, {
          showAllApplications: true
        }),
        discoveredDaysAgo: getDiscoveredDaysAgo(discoveredAt),
        relatedImagesCount: resources.filter(
          ({ type }) => type === "ImageRepository"
        ).length,
        relatedVMsCount: resources.filter(({ type }) => type === "Vm").length,
        riskInsights: {
          id: `${key}_riskInsights`,
          pocExploitMaturity: riskInsights.exploitMaturities.includes("POC"),
          inTheWildExploitMaturity:
            riskInsights.exploitMaturities.includes("IN_THE_WILD"),
          ...getExploitabilityByKeys(riskInsights.exploitMaturities),
          ...riskInsights
        },
        expandable: issuesCounts.length > 0,
        rowData: { id: key },
        hasTicketAction,
        ticketSystems,
        hasPullRequestAction,
        ...restItem
      }
    }),
    ...restResponseData
  }
}

// Tickets carry no vendor; the URL tells which system each lives in
export const getIssueTicketSystems = (tickets: Ticket[] = []) =>
  getTicketingSystemsFromUrls(tickets.map(({ url }) => url))

// A project's ticket actions carry only a URL, like an issue's tickets
export const getProjectTicketSystems = (actions: ProjectAction[] = []) =>
  getTicketingSystemsFromUrls(
    actions.filter(({ type }) => type === "ticket").map(({ url }) => url)
  )

export const getIssuesTriageSelectorCommon = (issues: Issue[]) =>
  (issues || []).map((item) => {
    const {
      discoveredAt,
      riskId,
      relatedApplications,
      packageName,
      riskInsights,
      introducedThroughInsights,
      severity,
      fixInsights,
      discoveredAtInUserLocale,
      resolvedAtInUserLocale,
      pullRequests,
      ...rest
    } = item

    const introducedThroughInsightsObject = {
      id: `${item.id}_introducedThroughInsights`,
      allFalse: !Object.keys(introducedThroughInsights).some(
        (key) => !!(introducedThroughInsights as any)[key]
      ),
      ...introducedThroughInsights
    }

    const fixInsightsObject = {
      id: `${item.id}_fixInsights`,
      introducedThroughInsights: introducedThroughInsightsObject,
      allFalse: !Object.keys(fixInsights).some(
        (key) => key !== "remediationEffort" && !!(fixInsights as any)[key]
      ),
      knownRemediationEffort:
        fixInsights.remediationEffort === "patch" ||
        fixInsights.remediationEffort === "minor" ||
        fixInsights.remediationEffort === "major",
      someIntroducedThroughInsightsExist:
        !introducedThroughInsightsObject.allFalse,
      ...fixInsights
    }

    const aggregatedKaiFindings = {
      irrelevant: [],
      positive: [],
      undetermined: []
    } as Record<"irrelevant" | "positive" | "undetermined", FindingKai[]>

    const dataflowAnalysisByCoordinateId = {} as Record<string, DataFlow>

    ;(rest.findings || []).forEach(({ kaiAnalysis, resource, ...finding }) => {
      const { introducedThrough, dataflowAnalysis } = finding

      if (!introducedThrough?.length) {
        return
      }

      ;(dataflowAnalysis || []).forEach((item) => {
        const {
          llmIssueDescription,
          falsePositiveReason,
          isFalsePositive = null
        } = item.verdict || {}

        const description =
          isFalsePositive === true ? falsePositiveReason : llmIssueDescription

        dataflowAnalysisByCoordinateId[item.coordinateId] = {
          isFalsePositive,
          description: description || "",
          inProgress: item.inProgress ?? false,
          flows: item.flows.map((flow) => {
            const { isVulnerable, functions = [] } = flow || {}
            const items: Flows[] = []
            let itemIndex = -1
            let counter = 1

            functions.forEach((func, index) => {
              const roles = DataFlowDomain.getRoles(func)
              const root = DataFlowDomain.isRootFunction(index, functions)
              const el = root ? items : items[itemIndex]?.children

              if (el)
                el.push({
                  roles,
                  number: counter++,
                  shortName: func.functionShortName,
                  fullName: func.functionFullName,
                  file: func.file,
                  line: func.startingLine + 1,
                  summary: func.summary,
                  logicalActions: func.logicalActions,
                  isRoot: root,
                  children: [],
                  scmUrl: func.scmUrl ?? undefined
                })

              if (root) itemIndex += 1
            })

            return { isFalsePositive: !isVulnerable, items }
          })
        }
      })

      const kaiAnalysisFindingsByCoordinateId = (
        kaiAnalysis?.sastLlmFindings?.findings || []
      ).reduce(
        (acc, findingKai) => {
          acc[findingKai.coordinateId] = findingKai

          return acc
        },
        {} as Record<string, FindingKai>
      )

      const notOnPremSourceControl = !isOnPremSourceControl(resource)

      introducedThrough.forEach((item) => {
        if (item && !Array.isArray(item) && "coordinateId" in item) {
          const { coordinateId, url, displayText } = item

          if (
            coordinateId &&
            !kaiAnalysisFindingsByCoordinateId[coordinateId] &&
            notOnPremSourceControl
          )
            aggregatedKaiFindings["undetermined"].push({
              coordinateId,
              url,
              line:
                url?.split("#L")?.[1] || displayText?.split(":")?.[1] || "-", // extract line from url or displayText, from url after "#L" (GitHub-style line anchor) or from displayText after ":", fallback is "-"
              coordinates: "",
              description:
                "Kai analyzed this instance, but the signals weren’t reliable enough to decide whether it’s a confirmed issue or a false positive, likely influenced by large or complex code regions, overlapping findings, or other structural ambiguities.",
              falsePositiveReason: "",
              isFalsePositive: null,
              llmIssueDescription: "",
              dataflowAnalysis: dataflowAnalysisByCoordinateId[coordinateId],
              inProgress: finding.inProgress ?? false
            })
        }
      })

      const findingsKai = kaiAnalysis?.sastLlmFindings?.findings || []

      if (!findingsKai.length) return finding

      const introducedThroughIndexByCoordinateId =
        introducedThrough?.reduce(
          (acc, item) => {
            if (Array.isArray(item)) return acc

            if ("coordinateId" in item && item.coordinateId) {
              acc[item.coordinateId] = item
            }

            return acc
          },
          {} as Record<string, FixLocation>
        ) || {}

      findingsKai.forEach(({ url: _, ...findingKai }) => {
        try {
          let field: "irrelevant" | "positive" | "undetermined" =
            findingKai.isFalsePositive ? "irrelevant" : "positive"

          if (findingKai.isFalsePositive === null) field = "undetermined"

          const { url } =
            introducedThroughIndexByCoordinateId[findingKai.coordinateId] || {}
          const description =
            findingKai.falsePositiveReason || findingKai.llmIssueDescription
          const line =
            JSON.parse(findingKai.coordinates)?.[0]?.Start?.Line || "-"

          aggregatedKaiFindings[field].push({
            description,
            url,
            line,
            dataflowAnalysis:
              dataflowAnalysisByCoordinateId[findingKai.coordinateId],
            ...findingKai,
            inProgress: findingKai.inProgress ?? finding.inProgress ?? false
          })
        } catch {
          console.error("Error parsing kai findings")
        }
      })
    })

    const applicationCount = getApplicationsMetaData(relatedApplications, {
      showAllApplications: true,
      noData: IssueDomain.isCodeIssue(item)
    })
    const resource = item.findings?.[0]?.resource
    const typeResource = resource?.type
    const applicationsMeta = {
      isVM: typeResource === "Vm",
      isOnPrem:
        typeResource === "CodeRepository" && isOnPremSourceControl(resource)
    }

    return {
      riskId,
      discoveredAt,
      relatedApplications,
      aggregatedKaiFindings,
      dataflowAnalysisByCoordinateId,
      applicationCount,
      applicationsMeta,
      key: item.id,
      cve: riskId,
      issueTypeVulnerabilityCell: item.issueType,
      packageName: packageName,
      discoveredDaysAgo: getDiscoveredDaysAgo(discoveredAt),
      pullRequests: pullRequests || [],
      hasActions: pullRequests?.length > 0 || rest.tickets?.length > 0,
      ticketSystems: getIssueTicketSystems(rest.tickets),
      riskInsights: {
        id: `${item.id}_riskInsights`,
        pocExploitMaturity: riskInsights.exploitMaturity === "POC",
        inTheWildExploitMaturity:
          riskInsights.exploitMaturity === "IN_THE_WILD",
        ...getExploitabilityByKeys(riskInsights.exploitMaturity),
        ...riskInsights
      },
      introducedThroughInsights: introducedThroughInsightsObject,
      fixInsights: fixInsightsObject,
      expandable: true,
      severity: severity.toLowerCase() as Severity,
      discoveredAtInUserLocale: getDateInUserLocale(
        getDateFromSeconds(discoveredAt),
        DEFAULT_LONG_FORMAT
      ),
      resolvedAtInUserLocale: getDateInUserLocale(
        getDateFromSeconds(rest.resolutionData?.resolvedAt),
        DEFAULT_LONG_FORMAT
      ),
      ...rest
    }
  })

export const getIssuesTriageSelector = (response: IssuesTriageResponse) => {
  const { data = [], ...rest } = response || {}

  return {
    data: getIssuesTriageSelectorCommon(Array.isArray(data) ? data : [data]),
    ...rest
  }
}

export const getIssuesTriageSelectorLoadMore = (response: {
  pages: IssuesTriageResponse[]
}) => {
  const { pages, ...rest } = response

  return {
    pages: getIssuesTriageSelectorCommon(pages.flatMap((page) => page.data)),
    metadata: pages[0]?.metadata,
    ...rest
  }
}

type PropsFilter = {
  t: TFunction
  internetFacingFF?: boolean
  exploitabilityEnabled?: boolean
}

export const getFilters = ({
  t,
  internetFacingFF,
  exploitabilityEnabled
}: PropsFilter) =>
  [
    filtersHelper.getVulnerabilityIdFilter(t),
    filtersHelper.getCveIdFilter(t),
    filtersHelper.getSeverityFilter(t, []),
    filtersHelper.getIssueTypeFilter(t),
    filtersHelper.getPackageNameFilter(t),
    filtersHelper.getPackageVersionFilter(t),
    {
      key: "exploitMaturity",
      label: t("filters.exploitability"),
      type: "multiple",
      options: EXPLOITABILITY_FILTER_OPTIONS
    },
    {
      key: "exploitabilityVerdict",
      label: t("filters.exploitabilityVerdict"),
      type: "multiple",
      options: EXPLOITABILITY_VERDICT_SEARCH_OPTIONS
    },
    {
      key: "runtime",
      label: t("filters.runtime"),
      type: "single",
      order: 1,
      options: [
        { label: "Yes", value: "YES" },
        { label: "No", value: "NO" },
        { label: "N/A", value: "UNDETERMINED" }
      ]
    },
    {
      key: "ingress",
      label: t("filters.ingress"),
      type: "single",
      order: 3,
      options: [
        { label: "Yes", value: "YES" },
        { label: "No", value: "NO" },
        { label: "N/A", value: "UNDETERMINED" }
      ]
    },
    {
      key: "internetFacing",
      label: t("filters.internetFacing"),
      type: "single",
      order: 2,
      options: [
        { label: "Yes", value: "YES" },
        { label: "No", value: "NO" },
        { label: "N/A", value: "UNDETERMINED" }
      ]
    },
    filtersHelper.getHighProfileFilter(t),
    {
      key: "kodemScore",
      label: t("filters.kodemScore"),
      type: "inputNumber",
      validation: (value: number) =>
        !isNaN(value) && value >= 0 && value <= 1000,
      errorMessage: "Insert value between 0 to 1000",
      helper: [
        { description: "Is greater or equal to a number between 0 - 1000" }
      ]
    },
    {
      key: "codeRepository",
      label: t("filters.codeRepository"),
      type: "input",
      helper: [
        {
          label: "code-repository:value1,value2",
          description:
            "Matches Code repository names as a substring (e.g. users-api,my-repo/)"
        }
      ]
    },
    {
      key: "imageId",
      label: t("filters.image"),
      type: "input",
      helper: [
        {
          label: "image:value1,value2",
          description:
            "Matches images name:tag as a substring (e.g. ubuntu,busybox:1.33.1)"
        }
      ]
    },
    filtersHelper.getApplications(t),
    {
      key: "environment",
      label: t("filters.environment"),
      type: "multiple",
      helper: [
        {
          label: "environment:value1,value2",
          description: "Equals value1 OR value2"
        }
      ],
      options: []
    },
    {
      key: "namespace",
      label: t("filters.namespace"),
      type: "input",
      helper: [
        {
          label: "namespace:value1,value2",
          description: "Matches namespaces as a substring (e.g. users,system)"
        }
      ]
    },
    {
      key: "projectResources",
      label: t("filters.resources").toLowerCase(),
      displayLabel: t("filters.resources"),
      type: "input",
      helper: [
        {
          label: t("filters.helpers.multipleValues", {
            label: t("filters.resources")
          }),
          description: t("filters.helpers.multipleSubstring", {
            label: "resources"
          })
        }
      ]
    },
    {
      key: "projectName",
      label: t("filters.triageProjectName").toLowerCase(),
      displayLabel: t("filters.triageProjectName"),
      type: "input",
      helper: [
        {
          label: t("filters.helpers.multipleValues", {
            label: t("filters.triageProjectName")
          }),
          description: t("filters.helpers.multipleSubstring", {
            label: "project names"
          })
        }
      ]
    },
    filtersHelper.getIssueStatusFilter(t),
    filtersHelper.getCweIdFilter(t),
    {
      key: "cweDescription",
      label: t("filters.cweName").toLowerCase(),
      displayLabel: t("filters.cweName"),
      type: "input",
      helper: [
        {
          label: t("filters.helpers.multipleValues", {
            label: t("filters.cweName")
          }),
          description: 'Matches multiple cwe name (e.g. "Prototype Pollution")'
        }
      ]
    },
    {
      key: "fromBaseImage",
      label: t("filters.fromBaseImage"),
      order: 6,
      type: "single",
      options: [
        { label: "Yes", value: "YES" },
        { label: "No", value: "NO" },
        { label: "N/A", value: "UNDETERMINED", disabled: true }
      ]
    },
    filtersHelper.getBaseImageNameFilter(t),
    filtersHelper.getBaseImageDigestFilter(t),
    {
      key: "fromThirdParty",
      label: t("filters.fromThirdParty"),
      order: 7,
      type: "single",
      options: [
        { label: "Yes", value: "YES" },
        { label: "No", value: "NO" },
        { label: "N/A", value: "UNDETERMINED", disabled: true }
      ]
    },
    {
      key: "isDirect",
      label: t("filters.isDirect"),
      order: 4,
      type: "single",
      options: [
        { label: "Yes", value: "YES" },
        { label: "No", value: "NO" },
        { label: "N/A", value: "UNDETERMINED", disabled: true }
      ]
    },
    {
      key: "isIndirect",
      label: t("filters.isIndirect"),
      order: 5,
      type: "single",
      options: [
        { label: "Yes", value: "YES" },
        { label: "No", value: "NO" },
        { label: "N/A", value: "UNDETERMINED", disabled: true }
      ]
    },
    {
      key: "introducedThrough",
      label: t("filters.introducedThrough"),
      type: "input",
      linkedKey: "introducedThrough",
      condition: "AND",
      helper: [
        {
          label: "introduced-through:value1,value2",
          description:
            "Matches introduced through paths which include the provided substrings. Accepts package names, base images and code weakness locations"
        }
      ]
    },
    {
      key: "notIntroducedThrough",
      label: `${INEQUALITY_SYMBOL}${t("filters.introducedThrough")}`,
      type: "input",
      linkedKey: "introducedThrough",
      inequality: true,
      helper: [
        {
          label: "!introduced-through:value1,value2",
          description:
            "Matches introduced through paths which does NOT include the provided substrings. Accepts package names, base images and code weakness locations"
        }
      ]
    },
    {
      key: "owasp",
      label: t("filters.owasp").toLowerCase(),
      displayLabel: t("filters.owasp"),
      type: "multiple",
      helper: [
        {
          label: t("filters.helpers.multipleValues", {
            label: t("filters.owasp")
          }),
          description: "Equals value1 OR value2"
        }
      ],
      options: []
    },
    filtersHelper.getLabelsFilter(t),
    filtersHelper.getNotLabelsFilter(t),
    {
      key: "hasLabel",
      label: "has-label",
      type: "single",
      options: [
        { label: "Yes", value: "YES" },
        { label: "No", value: "NO" }
      ]
    },
    filtersHelper.getHasActionFilter(t),
    filtersHelper.getJiraIssueIdFilter(t),
    filtersHelper.getLanguagesFilter(t),
    filtersHelper.getHasFunctionEvidenceFilter(t),
    filtersHelper.getOsLanguagesFilter(t),
    filtersHelper.getDismissReasonFilter(t),
    filtersHelper.getDismissedByFilter(t),
    filtersHelper.getIsDevDependencyFilter(t),
    filtersHelper.getRuleIdFilter(t),
    filtersHelper.getFixVersionFilter(t),
    filtersHelper.getKodemRemediationFilter(t),
    filtersHelper.getRemediationEffortFilter(t),
    filtersHelper.getResolvedInScopeFilter(t),
    filtersHelper.getFixableFilter(t),
    filtersHelper.getKaiInsightFilter(t),
    filtersHelper.getKaiDataFlowFilter(t),
    filtersHelper.getResolvedTime(t),
    filtersHelper.getDiscoveredTime(t),
    filtersHelper.getDismissedTime(t),
    filtersHelper.getResolvedInCodeRepoFilter(t),
    {
      key: "quickWins",
      label: t("filters.quickWins"),
      type: "single",
      options: [{ label: "Yes", value: "YES" }]
    },
    {
      key: "quickWinsType",
      label: t("filters.quickWinsType"),
      type: "multiple",
      helper: [
        {
          label: t("filters.helpers.multipleValues", {
            label: t("filters.quickWinsType")
          }),
          description: t("filters.helpers.equals")
        }
      ],
      options: [
        { label: t("quickWins.filter.packageUpdate"), value: "PACKAGE_UPDATE" },
        {
          label: t("quickWins.filter.baseImageUpdate"),
          value: "BASE_IMAGE_UPDATE"
        }
      ]
    },
    filtersHelper.getPackageCategoryAI(t),
    filtersHelper.getIsPackageAiRelated(t)
  ].filter((filter) => {
    if (filter.key === "internetFacing" && !internetFacingFF) return false
    if (filter.key === "exploitabilityVerdict" && !exploitabilityEnabled)
      return false

    return true
  })

export const getFiltersTriageSelector = (
  response: {
    data: Record<string, (string | Label)[]>
  },
  props: PropsFilter
) => {
  const { data } = response
  return getFilters(props)
    .map((filter) => {
      const key = filter.linkedKey || filter.key
      const currentFilterData = data[key]
      const { options, type } = filter

      if (currentFilterData && !!options) {
        if (Array.isArray(options) && options.length > 0) {
          if (type === "single") {
            filter.options = options.map((value) => ({
              ...value,
              disabled: !currentFilterData.includes(value.value)
            }))

            return filter
          }

          filter.options = (options as Option[]).filter((value) =>
            currentFilterData.includes(value.value)
          )
        } else {
          filter.options = currentFilterData.map((value) => {
            if (typeof value !== "string") {
              const { name, color } = value

              return { color, label: name, value: name }
            }

            let label = value

            if (key === "issueType") {
              label = props.t(`issueTypes.${value}`)
            }

            if (key === "owasp") {
              label = `"${value}"`
            }

            if (key === "remediationEffort") {
              label = props.t(`remediation.effortTypes.${value}`)
            }

            return { label, value }
          })
        }
      }

      return filter
    })
    .reduce(
      (acc, filter) => {
        const key = filter.linkedKey || filter.key

        if (!!acc[key] && !!filter.options) {
          acc[key] = filter.options
        }

        acc.allFilters.push(filter as Filters[number])

        return acc
      },
      {
        allFilters: [],
        severity: [],
        issueType: [],
        exploitMaturity: [],
        issueStatus: [],
        label: [],
        osLanguages: []
      } as Record<string, Option[]> & { allFilters: Filters }
    )
}

export const projectUpdater = (
  projectId: string,
  queryClient: QueryClient,
  cb: (project: Project) => Project
) => {
  queryClient
    .getQueriesData<ProjectsTriageResponse>({
      queryKey: [RESOURCES_NAMES.TRIAGE.PROJECTS]
    })
    .forEach(([key, response]) => {
      const { data: dataInResponse = [] } = response || {}

      if (!dataInResponse?.length) return

      const index = dataInResponse.findIndex(({ id }) => id === projectId)

      if (index === -1) return

      queryClient.setQueryData<ProjectsTriageResponse | undefined>(
        key,
        (oldData) => {
          try {
            if (!oldData?.data?.length) return oldData

            const { data, ...restOldData } = oldData

            return {
              data: data.map((project, currentIndex) =>
                index === currentIndex ? cb(project) : project
              ),
              ...restOldData
            }
          } catch {
            return oldData
          }
        }
      )
    })
}
